import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CalendarDays, Plus, Sparkles, Users } from 'lucide-react';
import {
  ActionButton,
  ContextHero,
  DataCard,
  DateBadge,
  EmptyStateModern,
  KpiCard,
  MoneyAmount,
  PageContainer,
  SectionTitle,
  SkeletonShimmer,
  StatusBadge,
  type UniversalStatus,
} from '@/components/design-system';
import { useB2cActions, type B2cActionStatut } from '@/hooks/queries/useB2c';
import { B2cActionFormDialog } from './B2cActionFormDialog';

const STATUT_TONE: Record<B2cActionStatut, UniversalStatus> = {
  planifiee: 'planifie',
  realisee: 'paye',
  annulee: 'annule',
  reportee: 'en_attente',
};

const STATUT_LABEL: Record<B2cActionStatut, string> = {
  planifiee: 'Planifiée',
  realisee: 'Réalisée',
  annulee: 'Annulée',
  reportee: 'Reportée',
};

export function B2cDashboardPage(): JSX.Element {
  const [createOpen, setCreateOpen] = useState(false);
  const { data: actions = [], isLoading } = useB2cActions({});

  const stats = useMemo(() => {
    const now = new Date();
    const startWeek = new Date(now);
    startWeek.setDate(now.getDate() - 7);
    const startMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const semaine = actions.filter((a) => {
      const d = new Date(a.date_action);
      return d >= startWeek && d <= now;
    });
    const mois = actions.filter((a) => new Date(a.date_action) >= startMonth);
    const caMois = mois
      .filter((a) => a.statut === 'realisee')
      .reduce((s, a) => s + Number(a.prix_unitaire_b2c) * Number(a.capacite_max), 0);
    return { semaine: semaine.length, mois: mois.length, caMois };
  }, [actions]);

  return (
    <PageContainer>
      <div className="space-y-6">
        <ContextHero
          variant="formation_b2c"
          icon={<Sparkles className="h-5 w-5" />}
          title="Formation B2C"
          subtitle="Actions hebdomadaires : 1 séance = 1 formateur + N participants. Modèle simplifié, paiement formateur forfait ou %CA."
          actions={
            <ActionButton
              variant="gradient"
              module="formation_b2c"
              icon={<Plus className="h-4 w-4" />}
              onClick={() => setCreateOpen(true)}
            >
              Nouvelle action
            </ActionButton>
          }
        />

        <div className="grid gap-3 sm:grid-cols-3">
          <KpiCard
            variant="formation_b2c"
            label="Actions cette semaine"
            value={stats.semaine}
            icon={<CalendarDays className="h-4 w-4" />}
            loading={isLoading}
          />
          <KpiCard
            variant="formation_b2c"
            label="Actions ce mois"
            value={stats.mois}
            icon={<Users className="h-4 w-4" />}
            loading={isLoading}
          />
          <KpiCard
            variant="finances"
            label="CA potentiel mois (max)"
            value={<MoneyAmount amount={stats.caMois} variant="inline" />}
            icon={<Sparkles className="h-4 w-4" />}
            loading={isLoading}
          />
        </div>

        <SectionTitle>
          {actions.length} action{actions.length > 1 ? 's' : ''}
        </SectionTitle>

        {isLoading ? (
          <SkeletonShimmer variant="card" count={3} />
        ) : actions.length === 0 ? (
          <EmptyStateModern
            variant="formation_b2c"
            icon={<Sparkles className="h-7 w-7" />}
            title="Aucune action B2C planifiée"
            description="Créez une séance hebdomadaire (cours pizza, langue, sport…) — paiement formateur forfait OU % du CA."
            ctaLabel="Nouvelle action"
            onCtaClick={() => setCreateOpen(true)}
          />
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {actions.map((a, i) => (
              <motion.div
                key={a.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i, 8) * 0.03, ease: [0.16, 1, 0.3, 1] }}
              >
                <Link to={`/formation-b2c/${a.id}`}>
                  <DataCard interactive>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                          {a.reference}
                        </div>
                        <h3 className="mt-0.5 text-base font-bold text-slate-900">
                          <DateBadge date={a.date_action} format="long" />
                        </h3>
                        {a.horaire && (
                          <div className="mt-0.5 text-xs text-slate-500">{a.horaire}</div>
                        )}
                      </div>
                      <StatusBadge
                        status={STATUT_TONE[a.statut]}
                        label={STATUT_LABEL[a.statut]}
                        withDot
                      />
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                          Lieu
                        </div>
                        <div className="mt-0.5 line-clamp-1 text-slate-700">{a.lieu ?? '—'}</div>
                      </div>
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                          Prix
                        </div>
                        <MoneyAmount amount={a.prix_unitaire_b2c} variant="inline" />
                      </div>
                    </div>
                  </DataCard>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <B2cActionFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSaved={() => setCreateOpen(false)}
      />
    </PageContainer>
  );
}
